import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { isPhoneViewport } from '../../core/utils/phone';
import { ModalService } from '../modal';
import { MilaMultiSelectFetcher, MilaMultiSelectOption } from './mila-multi-select-option.model';
import { MilaMultiSelectSheet } from './mila-multi-select-sheet/mila-multi-select-sheet';

/**
 * Async, searchable multi-select for a flat list a combobox with the
 * selected items shown as removable chips inline, a debounced search-as-
 * you-type dropdown below it, and infinite scroll for paginated results.
 *
 * Deliberately source-agnostic: it doesn't know about HTTP or any specific
 * endpoint, it just calls whatever `fetchPage` it's given. The actual
 * "which backend endpoint, how to normalize the response" decisions live
 * one layer up, in whoever provides that fetcher (see
 * core/services/cikisi-resources.service.ts) this component only ever
 * deals with the normalized MilaMultiSelectOption shape.
 *
 * Plugs into a reactive form directly via `formControlName` (implements
 * ControlValueAccessor) the control's value is a MilaMultiSelectOption[].
 */
@Component({
  selector: 'mila-multi-select',
  standalone: false,
  templateUrl: './mila-multi-select.html',
  styleUrl: './mila-multi-select.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MilaMultiSelect),
      multi: true,
    },
  ],
})
export class MilaMultiSelect implements ControlValueAccessor, OnInit, OnDestroy {
  @Input({ required: true }) fetchPage!: MilaMultiSelectFetcher;
  @Input() placeholder = '';
  /** null = unlimited. Ignored when multiple is false. */
  @Input() maxSelected: number | null = null;
  /** false = picking a second option replaces the first instead of adding
   *  to the selection (and instead of blocking on maxSelected). */
  @Input() multiple = true;
  /** Heading of the phone bottom sheet; falls back to the placeholder. */
  @Input() sheetTitle = '';

  @ViewChild('input') private inputRef?: ElementRef<HTMLInputElement>;
  private readonly modalService = inject(ModalService);
  private readonly cdr = inject(ChangeDetectorRef);
  private sheetOpen = false;

  private readonly _destroy$ = new Subject<void>();
  private readonly _query$ = new Subject<string>();

  selected: MilaMultiSelectOption[] = [];
  options: MilaMultiSelectOption[] = [];
  queryText = '';
  isOpen = false;
  isLoading = false;
  hasMore = false;
  hoveredIndex = -1;
  limitMessage = '';

  private page = 0;
  private disabled = false;

  private onChange: (value: MilaMultiSelectOption[]) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this._query$
      .pipe(debounceTime(250), distinctUntilChanged(), takeUntil(this._destroy$))
      .subscribe(query => this.loadPage(query, 0, true));
  }

  // ── ControlValueAccessor ────────────────────────────────────────────
  writeValue(value: MilaMultiSelectOption[] | null): void {
    this.selected = value ?? [];
  }

  registerOnChange(fn: (value: MilaMultiSelectOption[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ── Open / close ─────────────────────────────────────────────────────
  open(): void {
    if (this.disabled || this.isOpen || this.sheetOpen) return;
    if (isPhoneViewport()) {
      this.openSheet();
      return;
    }
    this.isOpen = true;
    this.hoveredIndex = -1;
    if (!this.options.length) this.loadPage('', 0, true);
  }

  /** Phones: the dropdown would hang off the field and run under the
   *  playground's sticky run bar, so the picking happens in a bottom sheet
   *  and the result comes back as this control's value. */
  private openSheet(): void {
    this.sheetOpen = true;
    // No keyboard over the field we're leaving; the sheet has its own search.
    this.inputRef?.nativeElement.blur();
    this.modalService.medium(MilaMultiSelectSheet, {
      data: {
        title: this.sheetTitle || this.placeholder,
        placeholder: this.placeholder,
        fetchPage: this.fetchPage,
        selected: this.selected,
        multiple: this.multiple,
        maxSelected: this.maxSelected,
      },
      autoFocus: 'dialog',
      // Focus going back to the input would fire its (focus)="open()" and
      // reopen the sheet straight away.
      restoreFocus: false,
    }).closed$.subscribe(result => {
      this.sheetOpen = false;
      if (result) {
        this.selected = result;
        this.emit();
      }
      this.cdr.markForCheck();
    });
  }

  /** Bound to (appClickOutside) on the component's own root in the template
   *  safe to call even when already closed. */
  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.hoveredIndex = -1;
    this.onTouched();
  }

  // ── Search + pagination ──────────────────────────────────────────────
  onInput(value: string): void {
    this.queryText = value;
    // Typing always shows the panel don't rely solely on the earlier
    // (focus) event having left isOpen true; whatever else could flip it,
    // actively typing into the field should never leave the list hidden.
    if (!this.isOpen && !this.disabled) {
      this.isOpen = true;
    }
    this._query$.next(value);
  }

  onListScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      this.loadMore();
    }
  }

  loadMore(): void {
    if (!this.hasMore || this.isLoading) return;
    this.loadPage(this.queryText, this.page + 1, false);
  }

  private loadPage(query: string, page: number, reset: boolean): void {
    if (this.isLoading) return;
    this.isLoading = true;

    this.fetchPage(query, page)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: res => {
          this.options = reset ? res.items : [...this.options, ...res.items];
          this.hasMore = res.hasMore;
          this.page = page;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.hasMore = false;
        },
      });
  }

  /** Options already selected don't show again in the dropdown. */
  get visibleOptions(): MilaMultiSelectOption[] {
    const selectedIds = new Set(this.selected.map(o => o.id));
    return this.options.filter(o => !selectedIds.has(o.id));
  }

  // ── Selection ────────────────────────────────────────────────────────
  select(option: MilaMultiSelectOption): void {
    if (this.selected.some(o => o.id === option.id)) return;

    if (!this.multiple) {
      // Single-select: picking one swaps out whatever was there, never
      // blocked by maxSelected (that's a multi-select-only concept).
      this.limitMessage = '';
      this.selected = [option];
      this.emit();
      this.resetSearchIfAny();
      return;
    }

    if (this.maxSelected != null && this.selected.length >= this.maxSelected) {
      this.limitMessage = `You can add up to ${this.maxSelected} items.`;
      return;
    }

    this.limitMessage = '';
    this.selected = [...this.selected, option];
    this.emit();
    this.resetSearchIfAny();
  }

  /** Without this, the typed search text (and the list filtered down to
   *  it) just sits there after picking one nothing looked "broken", the
   *  next pick just wasn't visible until you cleared the field yourself. */
  private resetSearchIfAny(): void {
    if (this.queryText) {
      this.queryText = '';
      this.loadPage('', 0, true);
    }
    this.hoveredIndex = -1;
  }

  remove(option: MilaMultiSelectOption, event?: Event): void {
    event?.stopPropagation();
    this.selected = this.selected.filter(o => o.id !== option.id);
    this.limitMessage = '';
    this.emit();
  }

  private emit(): void {
    this.onChange(this.selected);
    this.onTouched();
  }

  // ── Keyboard ─────────────────────────────────────────────────────────
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!this.isOpen) { this.open(); return; }
      this.hoveredIndex = Math.min(this.hoveredIndex + 1, this.visibleOptions.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.hoveredIndex = Math.max(this.hoveredIndex - 1, 0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = this.visibleOptions[this.hoveredIndex];
      if (option) this.select(option);
    } else if (event.key === 'Backspace' && !this.queryText && this.selected.length) {
      this.remove(this.selected[this.selected.length - 1]);
    } else if (event.key === 'Escape') {
      this.close();
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
